// CustomerInfoTab.tsx
import { FieldRow, Input } from "@racoon/ui";
import { useT } from "@racoon/i18n";
import { Namespace } from "@racoon/i18n";
import { BIN_LENGTH, TEXT_FIELD_MAX_LENGTH } from "@racoon/lib";

interface CustomerInfoTabProps {
  name: string;
  bin: string;
  nameError?: string;
  binError?: string;
  jur_address: string;
  fact_address: string;
  directorLastName: string;
  directorFirstName: string;
  onNameChange: (name: string) => void;
  onBinChange: (bin: string) => void;
  onJurAddressChange: (jur_address: string) => void;
  onFactAddressChange: (fact_address: string) => void;
  onDirectorLastNameChange: (directorLastName: string) => void;
  onDirectorFirstNameChange: (directorFirstName: string) => void;
  /** The provider's customer type field, for an existing customer only. */
  customerTypeField?: React.ReactNode;
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="pt-2 text-xs font-bold uppercase tracking-wide text-text-primary">{children}</p>
);

const FieldError: React.FC<{ id: string; children: string }> = ({ id, children }) => (
  <p id={id} className="pt-1 text-xs text-brand-red">
    {children}
  </p>
);

export const CustomerInfoTab: React.FC<CustomerInfoTabProps> = ({
  name,
  bin,
  nameError,
  binError,
  jur_address,
  fact_address,
  directorLastName,
  directorFirstName,
  onNameChange,
  onBinChange,
  onJurAddressChange,
  onFactAddressChange,
  onDirectorLastNameChange,
  onDirectorFirstNameChange,
  customerTypeField,
}) => {
  const t = useT();

  return (
    <div className="space-y-4">
      <SectionHeading>{t(Namespace.Customers, "card.section.legal")}</SectionHeading>

      <FieldRow label={t(Namespace.Customers, "card.legalName.label")} htmlFor="org-name" alignTop>
        <div>
          <Input
            id="org-name"
            placeholder={t(Namespace.Customers, "card.legalName.placeholder")}
            value={name}
            maxLength={TEXT_FIELD_MAX_LENGTH}
            aria-invalid={!!nameError}
            aria-describedby={nameError ? "org-name-error" : undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
          />
          {nameError && <FieldError id="org-name-error">{nameError}</FieldError>}
        </div>
      </FieldRow>

      <FieldRow label={t(Namespace.Customers, "card.bin.label")} htmlFor="org-bin" alignTop>
        <div>
          <Input
            id="org-bin"
            placeholder={t(Namespace.Customers, "card.bin.placeholder")}
            value={bin}
            inputMode="numeric"
            maxLength={BIN_LENGTH}
            aria-invalid={!!binError}
            aria-describedby={binError ? "org-bin-error" : undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onBinChange(e.target.value)}
          />
          {binError && <FieldError id="org-bin-error">{binError}</FieldError>}
        </div>
      </FieldRow>

      {customerTypeField}

      <FieldRow label={t(Namespace.Customers, "card.jur_address.label")} htmlFor="jur-address">
        <Input
          id="jur-address"
          placeholder={t(Namespace.Customers, "card.jur_address.placeholder")}
          value={jur_address}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onJurAddressChange(e.target.value)}
        />
      </FieldRow>

      {/* Not present in the Figma frame, but kept so `fact_address` remains
          editable — it has no other entry point in the app. */}
      <FieldRow label={t(Namespace.Customers, "card.fact_address.label")} htmlFor="fact-address">
        <Input
          id="fact-address"
          placeholder={t(Namespace.Customers, "card.fact_address.placeholder")}
          value={fact_address}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFactAddressChange(e.target.value)}
        />
      </FieldRow>

      <SectionHeading>{t(Namespace.Customers, "card.section.director")}</SectionHeading>

      <FieldRow
        label={t(Namespace.Customers, "card.director.lastName.label")}
        htmlFor="director-last"
      >
        <Input
          id="director-last"
          placeholder={t(Namespace.Customers, "card.director.lastName.placeholder")}
          value={directorLastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onDirectorLastNameChange(e.target.value)
          }
        />
      </FieldRow>

      <FieldRow
        label={t(Namespace.Customers, "card.director.firstName.label")}
        htmlFor="director-first"
      >
        <Input
          id="director-first"
          placeholder={t(Namespace.Customers, "card.director.firstName.placeholder")}
          value={directorFirstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onDirectorFirstNameChange(e.target.value)
          }
        />
      </FieldRow>
    </div>
  );
};
